import { Status, type Requirement } from "../../redux/interfaces/folder.types";

export type IdentityValidationActivity =
  | "uploading"
  | "validating"
  | "failed"
  | null;

export interface IdentityValidationActivityUpdate {
  requirementId: string;
  activity: IdentityValidationActivity;
}

export function isIdentityValidationActivityProcessing(
  activity: IdentityValidationActivity,
): boolean {
  return activity === "uploading" || activity === "validating";
}

export function getIdentityValidationActivityLabel(
  activity: IdentityValidationActivity,
): string | null {
  if (activity === "uploading") return "Subiendo identificación";
  if (activity === "validating") return "Validando identificación";
  return null;
}

type PersistedIdentityValidationState = Pick<
  Requirement,
  "status" | "workflow_step"
>;

const PERSISTED_PROCESSING_STEPS = new Set(["upload", "validating"]);
const PERSISTED_SETTLED_STEPS = new Set([
  "address_check",
  "pending_review",
  "rejected",
  "completed",
  "data_confirmation",
  "data_edit",
]);

/**
 * Whether fresh folder data is sufficient to replace a session-local failure.
 * Processing steps always win, even if the requirement still carries a status
 * from an earlier attempt.
 */
export function hasSettledPersistedIdentityValidation(
  requirement: PersistedIdentityValidationState | null | undefined,
): boolean {
  if (!requirement) return false;

  const workflowStep = requirement.workflow_step ?? null;
  if (workflowStep && PERSISTED_PROCESSING_STEPS.has(workflowStep)) {
    return false;
  }
  if (workflowStep && PERSISTED_SETTLED_STEPS.has(workflowStep)) {
    return true;
  }

  return (
    requirement.status === Status.Valid ||
    requirement.status === Status.Invalid ||
    requirement.status === Status.PendingReview
  );
}
